import { Layout, Menu } from "antd"
import PropTypes from "prop-types"
import React from "react"
import { Route, Switch, useLocation } from "react-router"
import { Link } from "react-router-dom"
import styled from "styled-components"

import {
    ApartmentOutlined,
    CodeOutlined,
    MailOutlined,
    PaperClipOutlined,
    PoweroffOutlined,
} from "@ant-design/icons"

import { useStateValue } from "../../../_shared/context/AppStateStore"
import Logs from "./AppLogs"
import AdminBookings from "./Bookings"
import Documents from "./Documents"
import EmailTemplates from "./EmailTemplates"
import EventTypes from "./EventTypes"
import AdminEvents from "./Events"

// ----------------------------------------------------------------------------

function AdminLoggedApp(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { match } = props
    const { Content, Sider, Footer } = Layout

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const { pathname } = useLocation()

    const [, dispatch] = useStateValue()

    // -------------------------------------
    // Effects
    // -------------------------------------

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function getRoutes() {
        return (
            <Switch>
                <Route exact path={`${match.url}`} component={AdminEvents} />
                <Route
                    exact
                    path={`${match.url}/event-types`}
                    component={EventTypes}
                />
                <Route
                    exact
                    path={`${match.url}/bookings`}
                    component={AdminBookings}
                />
                <Route
                    exact
                    path={`${match.url}/documents`}
                    component={Documents}
                />
                <Route
                    exact
                    path={`${match.url}/email-templates`}
                    component={EmailTemplates}
                />
                <Route exact path={`${match.url}/logs`} component={Logs} />
            </Switch>
        )
    }

    function isRouteActive(path) {
        return pathname === path ? "ant-menu-item-selected" : ""
    }

    function handleSignOut() {
        dispatch({ type: "SIGN_OUT" })
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={props.className}>
            <Layout>
                <Sider className="sidebar" breakpoint="lg" collapsedWidth="0">
                    <Menu theme="dark" mode="inline">
                        <Menu.Item
                            key="events"
                            className={isRouteActive("/admin")}
                        >
                            <Link to="/admin" activeClassName="current-route">
                                <ApartmentOutlined />
                                <span className="menu-text">Eventi</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="events-types"
                            className={isRouteActive("/admin/event-types")}
                        >
                            <Link
                                to="/admin/event-types"
                                activeClassName="current-route"
                            >
                                <ApartmentOutlined />
                                <span className="menu-text">Tipologie</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="bookings"
                            className={isRouteActive("/admin/bookings")}
                        >
                            <Link
                                to="/admin/bookings"
                                activeClassName="current-route"
                            >
                                <ApartmentOutlined />
                                <span className="menu-text">Prenotazioni</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="documents"
                            className={isRouteActive("/admin/documents")}
                        >
                            <Link
                                to="/admin/documents"
                                activeClassName="current-route"
                            >
                                <PaperClipOutlined />
                                <span className="menu-text">Documenti</span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="templates"
                            className={isRouteActive("/admin/email-templates")}
                        >
                            <Link
                                to="/admin/email-templates"
                                activeClassName="current-route"
                            >
                                <MailOutlined />
                                <span className="menu-text">
                                    Template e-mail
                                </span>
                            </Link>
                        </Menu.Item>
                        <Menu.Item
                            key="logs"
                            className={isRouteActive("/admin/logs")}
                        >
                            <Link
                                to="/admin/logs"
                                activeClassName="current-route"
                            >
                                <CodeOutlined />
                                <span className="menu-text">Developer</span>
                            </Link>
                        </Menu.Item>
                    </Menu>
                    <div
                        className="signout ant-menu-item"
                        onClick={handleSignOut}
                    >
                        <PoweroffOutlined />
                        <span className="menu-text">Sign Out</span>
                    </div>
                </Sider>
                <Layout>
                    <div className="main-container">
                        <Content>{getRoutes()}</Content>
                        <Footer style={{ textAlign: "center" }}>
                            WelfareCare © {new Date().getFullYear()}
                        </Footer>
                    </div>
                </Layout>
            </Layout>
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

AdminLoggedApp.propTypes = {
    className: PropTypes.string.isRequired,
    match: PropTypes.object,
}

AdminLoggedApp.defaultProps = {}

// ----------------------------------------------------------------------------

const StyledAdminLoggedApp = styled(AdminLoggedApp)`
    & {
        min-height: 100vh;
        max-height: 100vh;
        min-width: 100vw;
        overflow-x: hidden;
        background-color: ${(props) =>
            props.theme.colors.primaryBackgroundColor};

        .ant-layout {
            min-height: 100vh;
            max-height: 100vh;

            background-color: ${(props) =>
                props.theme.colors.primaryBackgroundColor};

            aside {
                background: ${(props) => props.theme.colors.sidebarColor};
                background-color: transparent;
                border-radius: 0 20px 20px 0;

                .ant-layout-sider-children {
                    display: flex;
                    flex-direction: column;
                }

                .logo {
                    box-sizing: border-box;
                    padding: 20px;
                    min-height: 150px;

                    background-size: contain;
                    background-repeat: no-repeat;
                    background-origin: content-box;
                    background-position: center center;
                }

                .anticon.anticon-poweroff {
                    :hover {
                        color: #ffffff !important;
                    }
                }

                .signout {
                    color: ${(props) => props.theme.colors.white};
                    padding: 20px auto;
                    margin-left: 24px;
                    margin-bottom: 20px;
                }

                .divider {
                    margin: auto;
                    width: 90%;
                    height: 1px;
                    background: rgb(31, 40, 58);
                    box-shadow: rgba(255, 255, 255, 0.05) 0px 1px 0px;
                    border-radius: 30px;
                }

                .ant-menu {
                    padding-top: 100px;
                    flex: 1;
                    background-color: transparent;

                    .ant-menu-item {
                        position: relative;
                        padding-left: 22px;
                        padding-right: 0;

                        a {
                            padding-left: 22px;
                            color: ${(props) => props.theme.colors.white};

                            .anticon {
                                * {
                                    color: ${(props) =>
                                        props.theme.colors.white};
                                }
                            }

                            :hover {
                                text-decoration: none;
                            }
                        }

                        .menu-text {
                            color: ${(props) => props.theme.colors.white};
                            font-weight: normal;
                        }
                    }

                    .ant-menu-item:active {
                        background: none;
                    }

                    .ant-menu-item-selected {
                        position: relative;
                        padding-left: 0;
                        margin-left: 22px;

                        border-top-left-radius: 10px;
                        border-bottom-left-radius: 10px;
                        height: 40px;
                        width: calc(100% - 22px);

                        overflow: visible;
                        background-color: ${(props) =>
                            props.theme.colors.white};

                        ::before,
                        ::after {
                            box-sizing: border-box;
                        }

                        ::before {
                            content: "";
                            position: absolute;
                            height: 20px;
                            width: 10px;
                            background: transparent;
                            bottom: -20px;
                            right: 0px;
                            border-top-right-radius: 10px;
                            box-shadow: rgb(255, 255, 255) 1px -10px 0px 0px;
                        }

                        ::after {
                            content: "";
                            position: absolute;
                            height: 20px;
                            width: 10px;
                            background: transparent;
                            top: -20px;
                            right: 0px;
                            border-bottom-right-radius: 10px;
                            box-shadow: rgb(255, 255, 255) 1px 10px 0px 0px;
                        }

                        .current-route {
                            background: ${(props) =>
                                props.theme.colors.primaryBackgroundColor};
                        }
                        .anticon {
                            * {
                                fill: ${(props) =>
                                    props.theme.colors.textPrimary};
                            }
                        }
                        .menu-text {
                            font-weight: 600;
                            color: ${(props) =>
                                props.theme.colors.textPrimary} !important;
                        }
                    }
                }

                .sign-out-icon {
                    padding: 20px 0;
                }
            }

            .main-container {
                display: flex;
                flex-direction: column;

                height: 100%;

                overflow-y: auto;

                main {
                    flex: 1;
                    padding: 24px;
                    height: auto;
                    min-height: auto;
                }

                footer {
                    padding: 10px;
                    background-color: transparent;
                    color: #7f8fa4;
                    font-size: 10px;
                }
            }
        }
    }
`
// ----------------------------------------------------------------------------

export default StyledAdminLoggedApp
